import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { creditNoteService } from '../../../api/apiService';
import { sendCreditNoteCancellationEmails } from './creditNoteEmail';
import { useAuth } from '../../../contexts/AuthContext';
import '../../../App.css';





function MultiSelectFilter({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const shown = options.filter((o) => o.toLowerCase().includes(search.toLowerCase()));
  const toggle = (o) => onChange(selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className="cn-filter-select"
        onClick={() => { setOpen((v) => !v); setSearch(''); }}
        style={{ textAlign: 'left', cursor: 'pointer', fontWeight: selected.length > 0 ? 600 : 'normal' }}
      >
        {selected.length === 0 ? label : `${label}: ${selected.length}`} ▾
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1200, background: '#fff', border: '1px solid #ccc', minWidth: 230, maxHeight: 280, overflowY: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.18)' }}>
          <input
            autoFocus
            type="text"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', padding: '7px 8px', border: 'none', borderBottom: '1px solid #eee', boxSizing: 'border-box', fontSize: 12, outline: 'none' }}
          />
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              style={{ display: 'block', width: '100%', padding: '5px 8px', background: '#f5f5f5', border: 'none', borderBottom: '1px solid #eee', textAlign: 'left', cursor: 'pointer', fontSize: 11, color: '#c00' }}
            >
              Clear selection ({selected.length})
            </button>
          )}
          {shown.length === 0 ? (
            <div style={{ padding: 8, color: '#999', fontSize: 12 }}>No matches</div>
          ) : (
            shown.map((o) => (
              <label key={o} style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '4px 8px', fontSize: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={selected.includes(o)} onChange={() => toggle(o)} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/**
 * List all credit notes created. Export PDF and XRechnung XML (German e-invoice).
 * Includes filters (talent, agency, period) and CSV export.
 */
function CreditNotesList() {
  const { userRole } = useAuth();
  const canDelete = userRole === 'superadmin' || userRole === 'finance';

  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchLimit, setFetchLimit] = useState(200);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // 'delete-{id}'
  const [openMenu, setOpenMenu] = useState(null); // cn.id of open kebab menu
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  // Bulk PDF export: selected credit note ids + progress label ("3/12")
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkProgress, setBulkProgress] = useState(null);

  // Filters (talent/agency/client are multi-select arrays)
  const [filterSearch, setFilterSearch] = useState('');
  const [filterTalents, setFilterTalents] = useState([]);
  const [filterAgencies, setFilterAgencies] = useState([]);
  const [filterClients, setFilterClients] = useState([]);
  const [filterPeriod, setFilterPeriod] = useState('lastweek'); // all | lastweek | week | month | year | custom — defaults to last week
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterExported, setFilterExported] = useState('all'); // all | exported | not_exported
  const [filterExportedBy, setFilterExportedBy] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all | active | cancelled

  // Close kebab menu on outside click
  useEffect(() => {
    if (openMenu === null) return;
    const close = (e) => { setOpenMenu(null); };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [openMenu]);

  const doFetch = useCallback((limit) => {
    setLoading(true);
    setError(null);
    creditNoteService.getCreditNotes({ limit })
      .then((res) => setCreditNotes(res.data?.creditNotes || []))
      .catch((err) => {
        console.error('Error loading credit notes:', err);
        setError(err.message || 'Failed to load credit notes');
        setCreditNotes([]);
      })
      .finally(() => setLoading(false));
  }, []);

  // Initial load
  useEffect(() => {
    doFetch(200);
  }, []);

  // When period needs a larger dataset, bump the limit and re-fetch
  useEffect(() => {
    if ((filterPeriod === 'year' || filterPeriod === 'custom') && fetchLimit < 5000) {
      setFetchLimit(5000);
      doFetch(5000);
    }
  }, [filterPeriod]);

  const reload = () => doFetch(fetchLimit);

  const formatDate = (d) => {
    if (!d) return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    return isNaN(date.getTime()) ? String(d) : date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatTime = (d) => {
    if (!d) return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    if (isNaN(date.getTime())) return String(d);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatMoney = (n) => {
    if (n == null || n === '') return '—';
    const num = typeof n === 'number' ? n : parseFloat(n);
    return isNaN(num) ? String(n) : num.toFixed(2);
  };

  const displayAgency = (name) => {
    const s = name != null ? String(name).trim() : '';
    if (s.length <= 1) return '—';
    return s;
  };

  // Period helpers
  const getWeekBounds = () => {
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    return { from: monday, to: sunday };
  };

  const getLastWeekBounds = () => {
    const b = getWeekBounds();
    const from = new Date(b.from);
    from.setDate(from.getDate() - 7);
    const to = new Date(b.to);
    to.setDate(to.getDate() - 7);
    return { from, to };
  };

  const getMonthBounds = () => {
    const now = new Date();
    return {
      from: new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0),
      to: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999),
    };
  };

  const getYearBounds = () => {
    const now = new Date();
    return {
      from: new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0),
      to: new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999),
    };
  };

  const matchesPeriod = (cn) => {
    if (filterPeriod === 'all') return true;
    const dateStr = cn.created_at || cn.invoice_date;
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;
    if (filterPeriod === 'lastweek') { const b = getLastWeekBounds(); return d >= b.from && d <= b.to; }
    if (filterPeriod === 'week') { const b = getWeekBounds(); return d >= b.from && d <= b.to; }
    if (filterPeriod === 'month') { const b = getMonthBounds(); return d >= b.from && d <= b.to; }
    if (filterPeriod === 'year') { const b = getYearBounds(); return d >= b.from && d <= b.to; }
    if (filterPeriod === 'custom') {
      if (filterDateFrom) { const from = new Date(filterDateFrom); from.setHours(0, 0, 0, 0); if (d < from) return false; }
      if (filterDateTo) { const to = new Date(filterDateTo); to.setHours(23, 59, 59, 999); if (d > to) return false; }
      return true;
    }
    return true;
  };

  // Smart dropdown options — derived from loaded data
  const uniqueTalents = useMemo(() => {
    const s = new Set(creditNotes.map(cn => cn.talent_name).filter(Boolean));
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [creditNotes]);

  const uniqueAgencies = useMemo(() => {
    const s = new Set(
      creditNotes
        .map(cn => (cn.agency_name != null ? String(cn.agency_name).trim() : ''))
        .filter(v => v.length > 1)
    );
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [creditNotes]);

  /**
   * Extract client name from a credit note object.
   * client_name lives on credit_note_items in the DB, so the backend must JOIN it.
   * We try several paths in case the backend returns it differently:
   *   cn.client_name          — backend JOINs items and aliases to root level (preferred)
   *   cn.items[0].client_name — backend returns nested items array
   *   cn.client               — backend returns raw booking client field
   */
  const getClientName = (cn) => {
    if (cn.client_name) return cn.client_name;
    if (Array.isArray(cn.items) && cn.items[0]?.client_name) return cn.items[0].client_name;
    if (typeof cn.client === 'string' && cn.client.length > 1) return cn.client;
    if (cn.client && typeof cn.client === 'object') return cn.client.name || cn.client.label || null;
    return null;
  };

  const uniqueClients = useMemo(() => {
    const s = new Set(creditNotes.map(cn => getClientName(cn)).filter(Boolean));
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [creditNotes]);

  const uniqueExporters = useMemo(() => {
    const s = new Set(creditNotes.map(cn => cn.exported_by).filter(Boolean));
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [creditNotes]);

  // Filtered list — includes cancelled (greyed out, filterable via status filter)
  const filtered = useMemo(() => {
    const search = filterSearch.toLowerCase();
    return creditNotes.filter(cn => {
      if (filterStatus === 'active' && cn.status === 'cancelled') return false;
      if (filterStatus === 'cancelled' && cn.status !== 'cancelled') return false;
      if (filterTalents.length > 0 && !filterTalents.includes(cn.talent_name)) return false;
      if (filterAgencies.length > 0) {
        const ag = cn.agency_name != null ? String(cn.agency_name).trim() : '';
        if (!filterAgencies.includes(ag)) return false;
      }
      if (filterClients.length > 0 && !filterClients.includes(getClientName(cn))) return false;
      if (filterExported === 'exported' && !cn.export_date) return false;
      if (filterExported === 'not_exported' && cn.export_date) return false;
      if (filterExportedBy && cn.exported_by !== filterExportedBy) return false;
      if (search) {
        const haystack = [cn.credit_note_number, cn.talent_name, cn.agency_name, getClientName(cn), cn.created_by]
          .filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return matchesPeriod(cn);
    });
  }, [creditNotes, filterSearch, filterTalents, filterAgencies, filterClients, filterExported, filterExportedBy, filterStatus, filterPeriod, filterDateFrom, filterDateTo]);

  // Display order: a storno (…-S) is shown DIRECTLY below its cancelled original.
  // Stornos whose original is not in the current view keep their natural position.
  const displayList = useMemo(() => {
    const ids = new Set(filtered.map((cn) => cn.id));
    const stornoByOriginal = new Map();
    filtered.forEach((cn) => {
      if (cn.invoice_type === 'storno' && cn.related_credit_note_id && ids.has(cn.related_credit_note_id)) {
        stornoByOriginal.set(cn.related_credit_note_id, cn);
      }
    });
    const paired = new Set([...stornoByOriginal.values()].map((s) => s.id));
    const out = [];
    filtered.forEach((cn) => {
      if (paired.has(cn.id)) return; // rendered right below its original instead
      out.push(cn);
      const storno = stornoByOriginal.get(cn.id);
      if (storno) out.push({ ...storno, __pairedStorno: true });
    });
    return out;
  }, [filtered]);

  const cancelledNotes = useMemo(() =>
    creditNotes.filter(cn => cn.status === 'cancelled'),
  [creditNotes]);

  // Active = not cancelled — used for all totals and period cards
  const activeNotes = useMemo(() =>
    creditNotes.filter(cn => cn.status !== 'cancelled'),
  [creditNotes]);

  const sumMoney = (list, field) =>
    list.reduce((acc, cn) => acc + (parseFloat(cn[field]) || 0), 0);

  // Money totals exclude cancelled notes so cancelled/storno rows don't distort the sums
  const filteredTotals = useMemo(() => {
    const active = filtered.filter(cn => cn.status !== 'cancelled');
    return {
      count: filtered.length,
      cancelledCount: filtered.length - active.length,
      net: sumMoney(active, 'net_amount'),
      vat: sumMoney(active, 'vat_amount'),
      gross: sumMoney(active, 'gross_amount'),
    };
  }, [filtered]);

  const periodTotals = useMemo(() => {
    const inPeriod = (list, bounds) =>
      list.filter(cn => {
        const dateStr = cn.created_at || cn.invoice_date;
        if (!dateStr) return false;
        const d = new Date(dateStr);
        return !isNaN(d.getTime()) && d >= bounds.from && d <= bounds.to;
      });
    const week = inPeriod(activeNotes, getWeekBounds());
    const month = inPeriod(activeNotes, getMonthBounds());
    const year = inPeriod(activeNotes, getYearBounds());
    return {
      week:  { count: week.length,  gross: sumMoney(week, 'gross_amount') },
      month: { count: month.length, gross: sumMoney(month, 'gross_amount') },
      year:  { count: year.length,  gross: sumMoney(year, 'gross_amount') },
    };
  }, [activeNotes]);

  // CSV export
  const handleExportCsv = () => {
    const headers = ['Number', 'Type', 'Status', 'Working Dates', 'Created', 'Created By', 'Talent', 'Client', 'Agency', 'AP %', 'Net', 'VAT', 'Gross'];
    const escape = (v) => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = displayList.map(cn => [
      cn.credit_note_number || cn.id,
      cn.invoice_type || '',
      cn.status || '',
      cn.booking_dates ? cn.booking_dates.replace(/\n/g, ' | ') : formatDate(cn.invoice_date),
      cn.created_at ? `${formatDate(cn.created_at)} ${formatTime(cn.created_at)}` : '',
      cn.created_by || '',
      cn.talent_name || '',
      getClientName(cn) || '',
      displayAgency(cn.agency_name),
      cn.agency_percentage != null ? `${Number(cn.agency_percentage)}%` : '',
      formatMoney(cn.net_amount),
      formatMoney(cn.vat_amount),
      formatMoney(cn.gross_amount),
    ].map(escape).join(','));
    const csv = [headers.join(','), ...rows].join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv; charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `credit-notes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = async (cn) => {
    const key = `pdf-${cn.id}`;
    const wasAlreadyExported = !!cn.export_date;
    setDownloading((p) => ({ ...p, [key]: true }));
    try {
      const res = await creditNoteService.getCreditNotePdf(cn.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Gutschrift_${cn.credit_note_number || cn.id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      // Reload on first download so the lock status updates immediately
      if (!wasAlreadyExported) reload();
    } catch (err) {
      console.error('PDF download failed:', err);
    } finally {
      setDownloading((p) => ({ ...p, [key]: false }));
    }
  };

  // ── Bulk PDF export ──────────────────────────────────────────────────────────
  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const allFilteredSelected = filtered.length > 0 && filtered.every((cn) => selectedIds.has(cn.id));
  const toggleSelectAll = () => {
    setSelectedIds(allFilteredSelected ? new Set() : new Set(filtered.map((cn) => cn.id)));
  };

  /** Name of the archive, so a week's export is identifiable after the fact. */
  const zipFileName = () => {
    const stamp = (d) => d.toISOString().slice(0, 10);
    if (filterPeriod === 'lastweek' || filterPeriod === 'week') {
      const b = filterPeriod === 'lastweek' ? getLastWeekBounds() : getWeekBounds();
      return `Gutschriften_${stamp(b.from)}_bis_${stamp(b.to)}.zip`;
    }
    if (filterPeriod === 'custom' && (filterDateFrom || filterDateTo)) {
      return `Gutschriften_${filterDateFrom || 'anfang'}_bis_${filterDateTo || 'heute'}.zip`;
    }
    return `Gutschriften_${stamp(new Date())}.zip`;
  };

  /**
   * Accounting download: every selected credit note's PDF in ONE zip file.
   * Previously this fired a separate browser download per note, which meant confirming a
   * download burst and then sorting dozens of loose files by hand — the accounting
   * department asked for the week's documents as a single archive instead.
   *
   * jszip is imported on demand so it stays out of the main bundle; the archive is built in
   * memory, which is fine for the volumes here (a week's worth of small PDFs).
   */
  const handleDownloadSelectedPdfs = async () => {
    const toDownload = filtered.filter((cn) => selectedIds.has(cn.id));
    if (toDownload.length === 0 || bulkProgress) return;
    setError(null);
    const { default: JSZip } = await import('jszip');
    const zip = new JSZip();
    const failed = [];
    const used = new Set();
    for (let i = 0; i < toDownload.length; i++) {
      const cn = toDownload[i];
      const ref = cn.credit_note_number || cn.id;
      setBulkProgress(`${i + 1}/${toDownload.length}`);
      try {
        const res = await creditNoteService.getCreditNotePdf(cn.id);
        // A cancelled CN and its storno can share a stem; keep both instead of overwriting.
        let name = `Gutschrift_${ref}.pdf`;
        let n = 2;
        while (used.has(name)) name = `Gutschrift_${ref}_(${n++}).pdf`;
        used.add(name);
        zip.file(name, res.data);
      } catch (err) {
        console.error(`PDF download failed for ${ref}:`, err);
        failed.push(String(ref));
      }
    }
    if (used.size > 0) {
      // Note which ones are missing INSIDE the archive too — a short banner is easy to miss
      // once the zip is passed on to accounting.
      if (failed.length > 0) {
        zip.file('FEHLENDE_GUTSCHRIFTEN.txt',
          `These credit notes could not be exported and are NOT in this archive:\n${failed.join('\n')}\n`);
      }
      setBulkProgress('packing…');
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = zipFileName();
      a.click();
      window.URL.revokeObjectURL(url);
    }
    setBulkProgress(null);
    setSelectedIds(new Set());
    if (failed.length > 0) {
      setError(`${failed.length} of ${toDownload.length} PDF(s) could not be exported (${failed.join(', ')}) — see console.`);
    }
    reload(); // export locks may have changed
  };

  const handleCancel = async (cn) => {
    const num = cn.credit_note_number || cn.id;
    const pairNote = 'If this CN is part of a linked pair (talent CN + agency provision CN), BOTH will be cancelled together.';
    // A Stornogutschrift is created for EVERY cancellation, not only for exported notes: the
    // talent already received the PDF by email when the credit note was created, so the
    // document is out in the world and has to be reversed rather than quietly withdrawn.
    const msg = `Cancel credit note ${num}?\n\n`
      + `A Stornogutschrift (${num}-S) will be created automatically to reverse it, and emailed to the talent. `
      + `The document itself is not changed — the correction goes out as a new credit note with a new number.\n\n`
      + `${pairNote}\n\n`
      + `The linked bookings go back to Pending credit notes, ready for the corrected credit note.`;
    if (!window.confirm(msg)) return;
    setActionLoading(`cancel-${cn.id}`);
    setError(null);
    try {
      const res = await creditNoteService.cancelCreditNote(cn.id);
      await reload();

      // The talent holds the cancelled document, so send them the Storno. A linked pair
      // (talent CN + agency provision CN) is cancelled together and produces one Storno each,
      // so collect both — `linked` is the partner's own cancel result.
      const data = res?.data || {};
      const stornos = [data, data.linked]
        .filter((r) => r && r.stornoId)
        .map((r) => ({ creditNoteId: r.stornoId, creditNoteNumber: r.stornoNumber }));

      if (stornos.length > 0) {
        const mail = await sendCreditNoteCancellationEmails(stornos);
        if (mail.failed.length > 0) {
          // The cancellation itself went through — only the notification did not. Say so
          // precisely, otherwise someone will cancel a second time trying to "fix" it.
          setError(`Credit note ${num} was cancelled and ${mail.sent.length} Storno email(s) sent, `
            + `but ${mail.failed.length} FAILED: ${mail.failed.join('; ')}. `
            + `Send the Stornogutschrift to the talent manually.`);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to cancel');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (cn) => {
    if (cn.export_date && cn.status !== 'cancelled') {
      setError(`Credit note ${cn.credit_note_number || cn.id} is locked (exported on ${formatDate(cn.export_date)}). Cancel it first to create a corrected version.`);
      return;
    }
    if (!window.confirm(`Delete credit note ${cn.credit_note_number || cn.id}? If it is part of a linked pair (talent CN + agency provision CN), both are deleted. Bookings will be available again for creating a new credit note.`)) return;
    setActionLoading(`delete-${cn.id}`);
    setError(null);
    try {
      await creditNoteService.deleteCreditNote(cn.id);
      reload();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete');
    } finally {
      setActionLoading(null);
    }
  };


  const resetFilters = () => {
    setFilterSearch('');
    setFilterTalents([]);
    setFilterAgencies([]);
    setFilterClients([]);
    setFilterExported('all');
    setFilterExportedBy('');
    setFilterStatus('all');
    setFilterPeriod('lastweek'); // default view is last week
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  if (loading) {
    return (
      <div className="content-wrapper page">
        <div className="content-wrapper">
          <h1>Credit notes</h1>
          <p>Loading{fetchLimit > 200 ? ' full dataset…' : '…'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content-wrapper page">
      <div className="content-wrapper">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 4 }}>
          <h1 style={{ margin: 0 }}>Credit notes</h1>
          {cancelledNotes.length > 0 && (
            <span style={{ fontSize: 12, color: '#666' }}>
              {cancelledNotes.length} cancelled included below
            </span>
          )}
        </div>

        {/* Period report summary */}
        <div className="cn-report-row">
          <div className="cn-report-card">
            <span className="cn-report-label">This week</span>
            <span className="cn-report-count">{periodTotals.week.count} notes</span>
            <span className="cn-report-gross">{periodTotals.week.gross.toFixed(2)} €</span>
          </div>
          <div className="cn-report-card">
            <span className="cn-report-label">This month</span>
            <span className="cn-report-count">{periodTotals.month.count} notes</span>
            <span className="cn-report-gross">{periodTotals.month.gross.toFixed(2)} €</span>
          </div>
          <div className="cn-report-card">
            <span className="cn-report-label">This year</span>
            <span className="cn-report-count">{periodTotals.year.count} notes</span>
            <span className="cn-report-gross">{periodTotals.year.gross.toFixed(2)} €</span>
          </div>
          <div className="cn-report-card cn-report-card--total">
            <span className="cn-report-label">All loaded</span>
            <span className="cn-report-count">{activeNotes.length} notes</span>
            <span className="cn-report-gross">{sumMoney(activeNotes, 'gross_amount').toFixed(2)} €</span>
          </div>
        </div>

        {/* Filters */}
        <div className="cn-filters">
          <input
            className="cn-filter-input"
            type="text"
            placeholder="Search number…"
            value={filterSearch}
            onChange={e => setFilterSearch(e.target.value)}
          />
          <MultiSelectFilter label="Talents" options={uniqueTalents} selected={filterTalents} onChange={setFilterTalents} />
          <MultiSelectFilter label="Agencies" options={uniqueAgencies} selected={filterAgencies} onChange={setFilterAgencies} />
          <MultiSelectFilter label="Clients" options={uniqueClients} selected={filterClients} onChange={setFilterClients} />
          <select className="cn-filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">All statuses</option>
            <option value="active">Active only</option>
            <option value="cancelled">Cancelled only</option>
          </select>
          <select className="cn-filter-select" value={filterExported} onChange={e => setFilterExported(e.target.value)}>
            <option value="all">All (exported &amp; not)</option>
            <option value="not_exported">Not exported yet</option>
            <option value="exported">Exported</option>
          </select>
          <select className="cn-filter-select" value={filterExportedBy} onChange={e => setFilterExportedBy(e.target.value)}>
            <option value="">All exporters</option>
            {uniqueExporters.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
          <select className="cn-filter-select" value={filterPeriod} onChange={e => setFilterPeriod(e.target.value)}>
            <option value="all">All time</option>
            <option value="lastweek">Last week</option>
            <option value="week">This week</option>
            <option value="month">This month</option>
            <option value="year">This year</option>
            <option value="custom">Custom range</option>
          </select>
          {filterPeriod === 'custom' && (
            <>
              <input
                className="cn-filter-input cn-filter-date"
                type="date"
                value={filterDateFrom}
                onChange={e => setFilterDateFrom(e.target.value)}
              />
              <input
                className="cn-filter-input cn-filter-date"
                type="date"
                value={filterDateTo}
                onChange={e => setFilterDateTo(e.target.value)}
              />
            </>
          )}
          <button type="button" className="button cn-filter-reset" onClick={resetFilters}>
            Reset
          </button>
          <button type="button" className="button cn-export-csv-btn" onClick={handleExportCsv}>
            Export CSV ({filtered.length})
          </button>
          <button
            type="button"
            className="button cn-export-csv-btn"
            onClick={handleDownloadSelectedPdfs}
            disabled={selectedIds.size === 0 || !!bulkProgress}
            title="Packs the PDF of every selected credit note into one ZIP file. Use the checkbox column to select — the header checkbox selects everything the filters show, e.g. a whole week."
          >
            {bulkProgress ? `Packing ${bulkProgress}` : `Download PDFs as ZIP (${filtered.filter((cn) => selectedIds.has(cn.id)).length})`}
          </button>
        </div>

        {error && (
          <div style={{ padding: 12, marginBottom: 16, background: '#f8d7da', color: '#721c24', borderRadius: 6 }}>
            {error}
          </div>
        )}

        {/* Filtered totals */}
        <div className="cn-filtered-totals">
          <span>
            {filtered.length} credit note{filtered.length !== 1 ? 's' : ''}
            {filteredTotals.cancelledCount > 0 && (
              <span style={{ color: '#c00' }}> ({filteredTotals.cancelledCount} cancelled)</span>
            )}
          </span>
          <span>Net: <strong>{filteredTotals.net.toFixed(2)} €</strong></span>
          <span>VAT: <strong>{filteredTotals.vat.toFixed(2)} €</strong></span>
          <span>Gross: <strong>{filteredTotals.gross.toFixed(2)} €</strong></span>
          {filteredTotals.cancelledCount > 0 && (
            <span style={{ color: '#999', fontSize: 11 }}>Totals exclude cancelled</span>
          )}
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #dee2e6', textAlign: 'left' }}>
                <th style={{ padding: '8px 6px' }}>
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAll}
                    title="Select all filtered credit notes"
                    aria-label="Select all filtered credit notes"
                  />
                </th>
                <th style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>Number</th>
                <th style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>Type / Status</th>
                <th style={{ padding: '8px 6px' }}>Working Dates</th>
                <th style={{ padding: '8px 6px' }}>Created</th>
                <th style={{ padding: '8px 6px' }}>Exported</th>
                <th style={{ padding: '8px 6px' }}>Talent</th>
                <th style={{ padding: '8px 6px' }}>Client</th>
                <th style={{ padding: '8px 6px' }}>Agency</th>
                <th style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>AP %</th>
                <th style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>Net</th>
                <th style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>VAT</th>
                <th style={{ padding: '8px 6px', whiteSpace: 'nowrap' }} className="credit-notes-col-gross">Gross</th>
                <th style={{ padding: '8px 6px' }}>PDF</th>
                <th style={{ padding: '8px 6px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={15} style={{ padding: 24, textAlign: 'center', color: '#666' }}>
                    {creditNotes.length === 0
                      ? 'No credit notes yet. Create them from the confirmed bookings list.'
                      : 'No credit notes match the current filters.'}
                  </td>
                </tr>
              ) : (
                displayList.map((cn) => (
                  <tr key={cn.id} style={{ borderBottom: '1px solid #eee', ...(cn.status === 'cancelled' ? { color: '#999', background: '#fafafa' } : {}), ...(cn.__pairedStorno ? { borderTop: 'none' } : {}) }}>
                    <td style={{ padding: '6px 6px' }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(cn.id)}
                        onChange={() => toggleSelected(cn.id)}
                        aria-label={`Select credit note ${cn.credit_note_number || cn.id}`}
                      />
                    </td>
                    <td style={{ padding: '6px 6px', whiteSpace: 'nowrap', fontSize: 11 }}>
                      {cn.__pairedStorno ? <span style={{ color: '#dc3545' }}>↳ </span> : null}
                      {cn.credit_note_number || cn.id}
                    </td>
                    <td style={{ padding: '6px 6px' }}>
                      <div style={{ fontSize: 11 }}>{cn.invoice_type || '—'}</div>
                      <div style={{ fontSize: 11, color: cn.status === 'cancelled' ? '#c00' : cn.export_date ? '#666' : '#333' }}>{cn.status || '—'}</div>
                      {cn.status === 'cancelled' && (cn.cancelled_at || cn.cancelled_by) && (
                        <div style={{ fontSize: 10, color: '#999', whiteSpace: 'nowrap' }}>
                          {cn.cancelled_at ? formatDate(cn.cancelled_at) : ''} {cn.cancelled_by || ''}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '6px 6px', fontSize: 11 }}>
                      {cn.booking_dates ? (
                        <div className="credit-notes-booking-dates">
                          {cn.booking_dates.split(/\s*,\s*|\n/).filter(Boolean).map((date, i) => (
                            <div key={i}>{date.trim()}</div>
                          ))}
                        </div>
                      ) : formatDate(cn.invoice_date)}
                    </td>
                    <td style={{ padding: '6px 6px' }}>
                      <div style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{cn.created_at ? `${formatDate(cn.created_at)} ${formatTime(cn.created_at)}` : '—'}</div>
                      <div style={{ fontSize: 11, color: '#666' }}>{cn.created_by || ''}</div>
                    </td>
                    <td style={{ padding: '6px 6px' }}>
                      {cn.export_date ? (
                        <>
                          <div style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{formatDate(cn.export_date)}</div>
                          <div style={{ fontSize: 11, color: '#666' }}>{cn.exported_by || ''}</div>
                        </>
                      ) : <span style={{ color: '#aaa', fontSize: 11 }}>—</span>}
                    </td>
                    <td style={{ padding: '6px 6px', fontSize: 11 }}>{cn.talent_name || '—'}</td>
                    <td style={{ padding: '6px 6px', fontSize: 11 }}>{getClientName(cn) || '—'}</td>
                    <td style={{ padding: '6px 6px', fontSize: 11 }}>{displayAgency(cn.agency_name)}</td>
                    <td style={{ padding: '6px 6px', fontSize: 11 }}>{cn.agency_percentage != null ? `${Number(cn.agency_percentage)}%` : '—'}</td>
                    <td style={{ padding: '6px 6px', fontSize: 11, whiteSpace: 'nowrap' }}>{formatMoney(cn.net_amount)}</td>
                    <td style={{ padding: '6px 6px', fontSize: 11, whiteSpace: 'nowrap' }}>{formatMoney(cn.vat_amount)}</td>
                    <td style={{ padding: '6px 6px', fontSize: 11, whiteSpace: 'nowrap' }} className="credit-notes-col-gross">{formatMoney(cn.gross_amount)}</td>
                    <td style={{ padding: '6px 6px' }}>
                      <button
                        type="button"
                        onClick={() => handleDownloadPdf(cn)}
                        disabled={downloading?.[`pdf-${cn.id}`]}
                        className="button talent-cn-export-btn"
                      >
                        {downloading?.[`pdf-${cn.id}`] ? '…' : 'PDF'}
                      </button>
                    </td>
                    <td style={{ padding: '6px 6px' }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (openMenu === cn.id) { setOpenMenu(null); return; }
                          const rect = e.currentTarget.getBoundingClientRect();
                          setMenuPos({ top: rect.bottom, left: rect.left });
                          setOpenMenu(cn.id);
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, padding: '0 6px', lineHeight: 1 }}
                        title="Actions"
                      >
                        ⋮
                      </button>
                      {openMenu === cn.id && (
                        <div style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, background: '#fff', border: '1px solid #ccc', borderRadius: 4, zIndex: 9999, minWidth: 130, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                          <button
                            type="button"
                            onClick={() => { setOpenMenu(null); handleCancel(cn); }}
                            disabled={!!actionLoading || cn.status === 'cancelled'}
                            style={{ display: 'block', width: '100%', padding: '8px 14px', background: 'none', border: 'none', textAlign: 'left', cursor: cn.status === 'cancelled' ? 'not-allowed' : 'pointer', fontSize: 13, color: cn.status === 'cancelled' ? '#aaa' : undefined }}
                          >
                            {cn.status === 'cancelled' ? 'Cancelled' : 'Cancel'}
                          </button>
                          {canDelete && (
                            <button
                              type="button"
                              onClick={() => { setOpenMenu(null); handleDelete(cn); }}
                              disabled={!!actionLoading || (!!cn.export_date && cn.status !== 'cancelled')}
                              style={{ display: 'block', width: '100%', padding: '8px 14px', background: 'none', border: 'none', textAlign: 'left', cursor: (cn.export_date && cn.status !== 'cancelled') ? 'not-allowed' : 'pointer', fontSize: 13, color: (cn.export_date && cn.status !== 'cancelled') ? '#aaa' : '#c00' }}
                              title={(cn.export_date && cn.status !== 'cancelled') ? 'Locked — cancel first' : ''}
                            >
                              {(cn.export_date && cn.status !== 'cancelled') ? 'Locked' : 'Delete'}
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
}

export default CreditNotesList;